"use client"

import { useEffect, useState, type FormEvent, type ReactNode } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { formatRuDate, todayIso } from "@/lib/legal/calendar"
import {
  art395,
  contractual,
  courtFee,
  type CourtFeeResult,
  type PenaltyResult,
} from "@/lib/legal/penalty-calculator"
import { reachGoal } from "@/lib/marketing/metrika"

type Mode = "contractual" | "art395" | "fee"

type View =
  | { kind: "penalty"; result: PenaltyResult }
  | { kind: "fee"; result: CourtFeeResult }

function isMode(value: string): value is Mode {
  return value === "contractual" || value === "art395" || value === "fee"
}

function parseDecimal(raw: string): number | null {
  const normalized = raw.trim().replace(/[\s\u00a0]/g, "").replace(",", ".")
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null
  const value = Number(normalized)
  return Number.isFinite(value) ? value : null
}

function formatRub(value: number, fractionDigits: 0 | 2): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 6 }).format(value)
}

function formatDays(days: number): string {
  const mod10 = days % 10
  const mod100 = days % 100
  const word =
    mod10 === 1 && mod100 !== 11
      ? "день"
      : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
        ? "дня"
        : "дней"
  return `${new Intl.NumberFormat("ru-RU").format(days)} ${word}`
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

export function PenaltyCalculator() {
  const [mode, setMode] = useState<Mode>("contractual")
  const [amount, setAmount] = useState("")
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [dailyRate, setDailyRate] = useState("")
  const [cap, setCap] = useState("")
  const [view, setView] = useState<View | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setTo((current) => current || todayIso())
  }, [])

  function clearResult() {
    setView(null)
    setError(null)
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    const principal = parseDecimal(amount)
    if (principal === null) {
      setView(null)
      setError(mode === "fee" ? "Укажите цену иска" : "Укажите сумму долга")
      return
    }

    try {
      if (mode === "fee") {
        setView({ kind: "fee", result: courtFee(principal) })
        reachGoal("calc_used")
        return
      }

      if (!from || !to) {
        setView(null)
        setError("Укажите дату начала и дату окончания")
        return
      }

      if (mode === "art395") {
        setView({ kind: "penalty", result: art395(principal, from, to) })
        reachGoal("calc_used")
        return
      }

      const rate = parseDecimal(dailyRate)
      if (rate === null) {
        setView(null)
        setError("Укажите процент в день, например 0,1")
        return
      }
      const capRaw = cap.trim()
      const capPercent = capRaw === "" ? null : parseDecimal(capRaw)
      if (capRaw !== "" && capPercent === null) {
        setView(null)
        setError("Предел укажите числом, например 10")
        return
      }

      setView({
        kind: "penalty",
        result: contractual(principal, from, to, rate, capPercent),
      })
      reachGoal("calc_used")
    } catch (caught) {
      setView(null)
      setError(caught instanceof Error ? caught.message : "Не удалось посчитать")
    }
  }

  const amountLabel = mode === "fee" ? "Цена иска" : "Сумма долга"

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6">
      <Tabs
        value={mode}
        onValueChange={(value) => {
          if (!isMode(value)) return
          setMode(value)
          clearResult()
        }}
      >
        <TabsList className="grid h-auto w-full grid-cols-1 gap-1 sm:grid-cols-3">
          <TabsTrigger className="h-auto whitespace-normal px-3 py-2 text-center" value="contractual">
            Договорная неустойка
          </TabsTrigger>
          <TabsTrigger className="h-auto whitespace-normal px-3 py-2 text-center" value="art395">
            Проценты по ст. 395 ГК
          </TabsTrigger>
          <TabsTrigger className="h-auto whitespace-normal px-3 py-2 text-center" value="fee">
            Госпошлина арбитраж
          </TabsTrigger>
        </TabsList>

        <TabsContent value={mode} className="mt-4">
          <form className="space-y-4" noValidate onSubmit={onSubmit}>
            <Field id="calc-amount" label={amountLabel}>
              <Input
                id="calc-amount"
                inputMode="decimal"
                autoComplete="off"
                className="h-11 text-base"
                placeholder="100 000"
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value)
                  clearResult()
                }}
              />
            </Field>

            {mode === "fee" ? (
              <p className="text-sm text-muted-foreground">
                Шкала для имущественного иска в арбитражный суд. Дата обращения — сегодня.
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field id="calc-from" label="Дата начала">
                  <Input
                    id="calc-from"
                    type="date"
                    className="h-11 text-base"
                    value={from}
                    onChange={(event) => {
                      setFrom(event.target.value)
                      clearResult()
                    }}
                  />
                </Field>
                <Field id="calc-to" label="Дата окончания" hint="По умолчанию — сегодня. Обе даты входят в расчёт.">
                  <Input
                    id="calc-to"
                    type="date"
                    className="h-11 text-base"
                    value={to}
                    onChange={(event) => {
                      setTo(event.target.value)
                      clearResult()
                    }}
                  />
                </Field>
              </div>
            )}

            {mode === "contractual" ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field id="calc-rate" label="Процент в день" hint="0,1 — это 0,1% за каждый день просрочки.">
                  <Input
                    id="calc-rate"
                    inputMode="decimal"
                    autoComplete="off"
                    className="h-11 text-base"
                    placeholder="0,1"
                    value={dailyRate}
                    onChange={(event) => {
                      setDailyRate(event.target.value)
                      clearResult()
                    }}
                  />
                </Field>
                <Field id="calc-cap" label="Предел, % от долга" hint="Необязательно. Если итог выше, в расчёт войдёт предел.">
                  <Input
                    id="calc-cap"
                    inputMode="decimal"
                    autoComplete="off"
                    className="h-11 text-base"
                    placeholder="10"
                    value={cap}
                    onChange={(event) => {
                      setCap(event.target.value)
                      clearResult()
                    }}
                  />
                </Field>
              </div>
            ) : null}

            <Button type="submit" size="lg" className="w-full sm:w-auto">
              Рассчитать
            </Button>
          </form>
        </TabsContent>
      </Tabs>

      {error ? (
        <p className="mt-4 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <div className="mt-4" aria-live="polite">
        {view?.kind === "penalty" ? <PenaltyView result={view.result} /> : null}
        {view?.kind === "fee" ? <FeeView result={view.result} /> : null}
      </div>

      <p className="mt-4 text-sm text-muted-foreground">
        Это справочный расчёт, не юридическая консультация.
      </p>
    </div>
  )
}

function TransferButton() {
  return (
    <Button asChild className="w-full sm:w-auto">
      <Link href="/login?from=pretenziya&calc=1">Перенести расчёт в претензию</Link>
    </Button>
  )
}

function PenaltyView({ result }: { result: PenaltyResult }) {
  const rate = result.periods[0]?.ratePercent
  return (
    <div className="space-y-4 rounded-xl border border-border bg-background p-4">
      <div>
        <p className="text-sm text-muted-foreground">{result.label}</p>
        <p className="mt-1 text-3xl font-semibold tracking-tight text-foreground tabular-nums">
          {formatRub(result.total, 2)}
        </p>
        <p className="mt-1 text-sm text-foreground">
          {formatDays(result.days)}
          {result.capped ? ", сработал предел от суммы долга" : ""}
        </p>
        {result.formula === "contractual" && rate != null && !result.capped ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {formatRub(result.principal, 2)} × {formatPercent(rate)}% × {formatDays(result.days)}
          </p>
        ) : null}
      </div>

      {result.formula === "art395" ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] text-left text-sm">
            <thead className="text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-3 font-medium">Период</th>
                <th className="py-2 pr-3 font-medium">Дней</th>
                <th className="py-2 pr-3 font-medium">Ставка</th>
                <th className="py-2 pr-3 font-medium">Дней в году</th>
                <th className="py-2 font-medium">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {result.periods.map((period) => (
                <tr key={`${period.from}-${period.to}`} className="border-b border-border last:border-0">
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {formatRuDate(period.from)} — {formatRuDate(period.to)}
                  </td>
                  <td className="py-2 pr-3 tabular-nums">{period.days}</td>
                  <td className="py-2 pr-3 tabular-nums">{formatPercent(period.ratePercent)}%</td>
                  <td className="py-2 pr-3 tabular-nums">{period.daysInYear}</td>
                  <td className="py-2 tabular-nums">{formatRub(period.amount, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-sm text-muted-foreground">
            Сумма × ставка ÷ 100 × дни ÷ дни в году. Отрезок режется при смене ставки и на границе года.
          </p>
        </div>
      ) : null}

      {result.notes.length > 0 ? (
        <ul className="space-y-2">
          {result.notes.map((note) => (
            <li key={note} className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-foreground">
              {note}
            </li>
          ))}
        </ul>
      ) : null}

      {result.sourceName && result.sourceUpdatedAt ? (
        <p className="text-sm text-muted-foreground">
          Источник: {result.sourceName}, таблица на {formatRuDate(result.sourceUpdatedAt)}
        </p>
      ) : null}

      <TransferButton />
    </div>
  )
}

function FeeView({ result }: { result: CourtFeeResult }) {
  return (
    <div className="space-y-4 rounded-xl border border-border bg-background p-4">
      <div>
        <p className="text-sm text-muted-foreground">Госпошлина</p>
        <p className="mt-1 text-3xl font-semibold tracking-tight text-foreground tabular-nums">
          {formatRub(result.fee, 0)}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Цена иска {formatRub(result.claimAmount, 2)}. {result.basis}. Шкала с {formatRuDate(result.effectiveFrom)}.
        </p>
      </div>
      <TransferButton />
    </div>
  )
}
